// pages/Dashboard/Staff.js
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FaSearch,
  FaPlus,
  FaUserTie,
  FaTimes,
  FaPhone,
  FaEnvelope,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaEdit,
  FaTrash,
  FaIdBadge,
  FaToggleOn,
  FaToggleOff,
  FaUsers,
} from 'react-icons/fa';
import '../Css/Staff.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const ROLE_STYLES = {
  Stylist: { cls: 'role-stylist' },
  Beautician: { cls: 'role-beautician' },
  'Makeup Artist': { cls: 'role-makeup' },
  'Massage Therapist': { cls: 'role-massage' },
  'Nail Technician': { cls: 'role-nails' },
  Receptionist: { cls: 'role-reception' },
  Manager: { cls: 'role-manager' },
};

const ROLE_OPTIONS = [
  'Stylist',
  'Beautician',
  'Makeup Artist',
  'Massage Therapist',
  'Nail Technician',
  'Receptionist',
  'Manager',
];

const FILTERS = [
  { key: 'all', label: 'All Staff' },
  { key: 'Stylist', label: 'Stylist' },
  { key: 'Beautician', label: 'Beautician' },
  { key: 'Makeup Artist', label: 'Makeup Artist' },
  { key: 'Massage Therapist', label: 'Massage Therapist' },
  { key: 'Nail Technician', label: 'Nail Technician' },
  { key: 'Receptionist', label: 'Receptionist' },
  { key: 'Manager', label: 'Manager' },
];

const EMPTY_FORM = {
  name: '',
  phone: '',
  email: '',
  role: '',
  specialization: '',
  joinDate: '',
  salary: '',
  address: '',
  status: 'active',
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

function Staff() {
  const [staffList, setStaffList] = useState([]);
  const [stats, setStats] = useState({ totalStaff: 0, activeStaff: 0, totalRoles: 0, topRole: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (activeFilter !== 'all') params.append('role', activeFilter);

      const res = await fetch(`${baseurl}/staff?${params.toString()}`, {
        headers: authHeaders(),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || 'Failed to fetch staff');

      setStaffList(data.data.staff);
      setStats(data.data.stats);
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to fetch staff', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const filteredStaff = useMemo(() => staffList, [staffList]);

  const openAddDrawer = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setEditMode(false);
    setShowAddDrawer(true);
  };

  const openEditDrawer = (member) => {
    setForm({
      name: member.name,
      phone: member.phone,
      email: member.email || '',
      role: member.role,
      specialization: member.specialization || '',
      joinDate: member.join_date ? member.join_date.slice(0, 10) : '',
      salary: member.salary || '',
      address: member.address || '',
      status: member.status,
    });
    setEditingId(member.id);
    setEditMode(true);
    setSelectedStaff(null);
    setShowAddDrawer(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.role || !form.joinDate) {
      setError('Please fill in all required fields');
      toast('Please fill in all required fields', 'error');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        role: form.role,
        specialization: form.specialization,
        joinDate: form.joinDate,
        salary: form.salary ? Number(form.salary) : null,
        address: form.address,
        status: form.status,
      };

      const url = editMode ? `${baseurl}/staff/${editingId}` : `${baseurl}/staff`;

      const res = await fetch(url, {
        method: editMode ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save staff member');

      toast(data.message || (editMode ? 'Staff member updated successfully' : 'Staff member added successfully'), 'success');

      setShowAddDrawer(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await fetchStaff();
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to save staff member', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      const res = await fetch(`${baseurl}/staff/${member.id}/toggle-status`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to toggle status');

      toast(data.message || 'Staff status updated', 'success');

      await fetchStaff();
      setSelectedStaff(data.data.staff);
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to toggle status', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${baseurl}/staff/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to delete staff member');

      toast(data.message || 'Staff member deleted successfully', 'success');

      setSelectedStaff(null);
      await fetchStaff();
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to delete staff member', 'error');
    }
  };

  return (
    <div className="staff-page">
      {/* HEADER */}
      <div className="staff-header">
        <div>
          <h2>Staff</h2>
          <p>Manage your team, roles, and contact details</p>
        </div>
        <button className="btn-primary" onClick={openAddDrawer}>
          <FaPlus /> Add Staff
        </button>
      </div>

      {error && <div className="staff-error-banner">{error}</div>}

      {/* STATS STRIP */}
      <div className="stats-strip">
        <div className="stat-card">
          <span className="stat-value">{stats.totalStaff}</span>
          <span className="stat-label">Total Staff</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.activeStaff}</span>
          <span className="stat-label">Active Staff</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.totalRoles}</span>
          <span className="stat-label">Roles</span>
        </div>
        <div className="stat-card">
          <span className="stat-value stat-value-sm">{stats.topRole?.role || '-'}</span>
          <span className="stat-label">Most Common Role</span>
        </div>
      </div>

      {/* SEARCH + FILTERS */}
      <div className="staff-toolbar">
        <div className="input-group search-input">
          <FaSearch className="input-icon" />
          <input
            type="text"
            placeholder="Search by name, ID, or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`filter-chip ${activeFilter === f.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* STAFF TABLE */}
      <div className="staff-table-wrapper">
        <table className="staff-table">
          <thead>
            <tr>
              <th>Staff Member</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Join Date</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="staff-empty-row">
                  Loading staff...
                </td>
              </tr>
            )}

            {!loading &&
              filteredStaff.map((member) => (
                <tr key={member.id} className="staff-row">
                  <td>
                    <div className="table-staff-name">
                      <div className="staff-avatar">{initials(member.name)}</div>
                      <div>
                        <strong>{member.name}</strong>
                        <span className="table-sub">{member.employee_code}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-tag ${ROLE_STYLES[member.role]?.cls}`}>
                      {member.role}
                    </span>
                  </td>
                  <td>
                    <span className="table-phone">
                      <FaPhone /> {member.phone}
                    </span>
                  </td>
                  <td>
                    <span className="table-date">
                      <FaCalendarAlt /> {member.join_date ? new Date(member.join_date).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td>
                    {/* <span
                      className={`status-pill ${member.status === 'active' ? 'status-active' : 'status-inactive'}`}
                      onClick={() => handleToggleStatus(member)}
                    >
                      {member.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                      {member.status === 'active' ? 'Active' : 'Inactive'}
                    </span> */}


                    <span
                      className={`status-pill ${member.status === 'active' ? 'status-active' : 'status-inactive'}`}
                      
                    >
                      
                      {member.status === 'active' ? 'Active' : 'Inactive'}
                    </span>


                  </td>
                  <td>
                    <button className="table-view-btn" onClick={() => setSelectedStaff(member)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}

            {!loading && filteredStaff.length === 0 && (
              <tr>
                <td colSpan="6" className="staff-empty-row">
                  No staff members match your search
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* STAFF DETAILS DRAWER */}
      {selectedStaff && (
        <div className="drawer-overlay" onClick={() => setSelectedStaff(null)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="staff-avatar large">{initials(selectedStaff.name)}</div>
                <div>
                  <h3>{selectedStaff.name}</h3>
                  <span className={`role-tag ${ROLE_STYLES[selectedStaff.role]?.cls}`}>
                    {selectedStaff.role}
                  </span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setSelectedStaff(null)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-stat-row">
                <div className="drawer-stat-box">
                  <FaIdBadge className="drawer-stat-icon" />
                  <div>
                    <strong>{selectedStaff.employee_code}</strong>
                    <span>Employee ID</span>
                  </div>
                </div>
                <div className="drawer-stat-box">
                  <FaCalendarAlt className="drawer-stat-icon" />
                  <div>
                    <strong>{new Date(selectedStaff.join_date).toLocaleDateString()}</strong>
                    <span>Joined</span>
                  </div>
                </div>
                <div className="drawer-stat-box">
                  <FaMoneyBillWave className="drawer-stat-icon" />
                  <div>
                    <strong>{selectedStaff.salary ? `₹${Number(selectedStaff.salary).toLocaleString()}` : '-'}</strong>
                    <span>Salary</span>
                  </div>
                </div>
              </div>

              <div className="drawer-section">
                <h4>
                  <FaUsers /> Status
                </h4>
                <span
                  className={`status-pill ${selectedStaff.status === 'active' ? 'status-active' : 'status-inactive'}`}
                  onClick={() => handleToggleStatus(selectedStaff)}
                >
                  {selectedStaff.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
                  {selectedStaff.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="drawer-section">
                <h4>
                  <FaPhone /> Contact
                </h4>
                <p className="notes-text">{selectedStaff.phone}</p>
                {selectedStaff.email && (
                  <p className="notes-text">
                    <FaEnvelope /> {selectedStaff.email}
                  </p>
                )}
              </div>

              {selectedStaff.specialization && (
                <div className="drawer-section">
                  <h4>
                    <FaUserTie /> Specialization
                  </h4>
                  <p className="notes-text">{selectedStaff.specialization}</p>
                </div>
              )}

              {selectedStaff.address && (
                <div className="drawer-section">
                  <h4>
                    <FaMapMarkerAlt /> Address
                  </h4>
                  <p className="notes-text">{selectedStaff.address}</p>
                </div>
              )}
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width" onClick={() => openEditDrawer(selectedStaff)}>
                <FaEdit /> Edit Staff
              </button>
              <button className="btn-danger full-width" onClick={() => handleDelete(selectedStaff.id)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD / EDIT STAFF DRAWER */}
      {showAddDrawer && (
        <div className="drawer-overlay" onClick={() => setShowAddDrawer(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="staff-avatar large">
                  <FaPlus />
                </div>
                <div>
                  <h3>{editMode ? 'Edit Staff Member' : 'Add New Staff Member'}</h3>
                  <span className="table-sub">Enter staff details and role</span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setShowAddDrawer(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-section">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Priya Sharma"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                />
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. 9876543210"
                    value={form.phone}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label">Email (optional)</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="e.g. priya@example.com"
                    value={form.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={form.role}
                    onChange={(e) => handleFormChange('role', e.target.value)}
                  >
                    <option value="">Select a role</option>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Specialization (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Balayage, Keratin"
                    value={form.specialization}
                    onChange={(e) => handleFormChange('specialization', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Join Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.joinDate}
                    onChange={(e) => handleFormChange('joinDate', e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label">Salary (₹, optional)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 25000"
                    value={form.salary}
                    onChange={(e) => handleFormChange('salary', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section">
                <label className="form-label">Address (optional)</label>
                <textarea
                  className="form-textarea"
                  rows="3"
                  placeholder="Residential address"
                  value={form.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                ></textarea>
              </div>

              <div className="drawer-section">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={form.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width" onClick={() => setShowAddDrawer(false)}>
                Cancel
              </button>
              <button className="btn-primary full-width" onClick={handleSubmit} disabled={saving}>
                <FaPlus /> {saving ? 'Saving...' : editMode ? 'Update Staff' : 'Add Staff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Staff;